import React, { useState, useEffect, useRef } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import './Survey.css';
import surveyData from './surveyQuestions.json';
import SurveySubmitter from '../../components/SurveySubmitter';
import { RECAPTCHA_SITE_KEY } from '../../config/recaptcha';

const Survey = () => {
  const [language, setLanguage] = useState('es');
  const [gameStarted, setGameStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isTyping, setIsTyping] = useState(false);
  const [displayedMessage, setDisplayedMessage] = useState('');
  const [displayedQuestion, setDisplayedQuestion] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  const [gameStarting, setGameStarting] = useState(false);
  const [surveyCompleted, setSurveyCompleted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [displayedSubtitle, setDisplayedSubtitle] = useState('');
  const [isTypingSubtitle, setIsTypingSubtitle] = useState(false);
  const [subtitleCompleted, setSubtitleCompleted] = useState(false);
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(true);
  const [welcomeMessageCompleted, setWelcomeMessageCompleted] = useState(false);
  const [showContinueButton, setShowContinueButton] = useState(false);
  const [hasInitializedTyping, setHasInitializedTyping] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [otherInputs, setOtherInputs] = useState({});
  const [isAgeQuestion, setIsAgeQuestion] = useState(false);
  const [shuffledOptions, setShuffledOptions] = useState({});
  const typingIntervalRef = useRef(null);
  const hasStartedTyping = useRef(false);

  // Función para mezclar aleatoriamente un array, manteniendo "otros" al final
  const shuffleOptionsForQuestion = (options) => {
    // Separar opciones "otros" de las demás
    const otherOptions = options.filter(option => 
      option.value === 'otro' || option.value === 'otra' || 
      option.value === 'otros' || option.value === 'otras'
    );
    const regularOptions = options.filter(option => 
      option.value !== 'otro' && option.value !== 'otra' && 
      option.value !== 'otros' && option.value !== 'otras'
    );
    
    // Mezclar solo las opciones regulares
    const shuffled = [...regularOptions];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    
    // Agregar las opciones "otros" al final
    return [...shuffled, ...otherOptions];
  };

  // Función para obtener opciones ordenadas para una pregunta
  const getOptionsForQuestion = (question) => {
    // IDs de preguntas que deben mantener el orden original (edad, horas, USD)
    const keepOrderIds = ["2-edad", 11, 24];
    
    // Si la pregunta debe mantener el orden, devolver opciones originales
    if (keepOrderIds.includes(question.id)) {
      return question.options;
    }
    
    const questionKey = `${question.id}_${currentQuestion}`;
    
    // Si ya tenemos las opciones mezcladas para esta pregunta, las usamos
    if (shuffledOptions[questionKey]) {
      return shuffledOptions[questionKey];
    }
    
    // Si no, las generamos y las guardamos
    const newShuffledOptions = shuffleOptionsForQuestion(question.options);
    setShuffledOptions(prev => ({
      ...prev,
      [questionKey]: newShuffledOptions
    }));
    
    return newShuffledOptions;
  };

  // Función para validar formato de email
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Función para verificar si se puede proceder a la siguiente pregunta
  const canProceed = () => {
    const currentQ = questions[currentQuestion];
    const currentAnswer = answers[currentQ.id];
    
    // Validar si hay respuesta según el tipo de pregunta
    let hasAnswer;
    if (currentQ.type === 'multiple-select') {
      hasAnswer = currentAnswer && currentAnswer.length > 0;
    } else if (currentQ.type === 'conditional-email') {
      // Para conditional-email, requiere selección de opción
      // Si eligió "si", también requiere email válido
      if (currentAnswer && currentAnswer.choice) {
        if (currentAnswer.choice === 'si') {
          // Si eligió "sí", debe tener un email válido
          hasAnswer = currentAnswer.email && isValidEmail(currentAnswer.email);
        } else {
          // Si eligió "no", solo necesita la selección
          hasAnswer = true;
        }
      } else {
        hasAnswer = false;
      }
    } else {
      hasAnswer = currentAnswer;
    }
    
    return hasAnswer && showOptions;
  };

  const texts = {
    es: {
      title: 'Zenit AI Survey',
      subtitle: 'Zenit AI Lab es una start-up de tecnología que diseña y desarrolla soluciones de IA accesibles e innovadoras. Esta encuesta pretende recabar información que nos permitirá entender mejor las necesidades de profesionales y empresas en torno a la automatización, permitiéndonos mejorar nuestros productos. Muchas gracias por su tiempo.',
      startButton: 'Comenzar Survey',
      continueButton: 'Continuar',
      previous: 'Anterior',
      selectOption: 'Selecciona una opción:',
      selectMultiple: 'Selecciona todas las opciones que apliquen (puedes arrastrar para reordenar):',
      selectRating: 'Selecciona tu puntuación:',
      placeholder: 'Escribe tu respuesta...',
      otherPlaceholder: 'Especifica cuál...',
      thankYou: '¡Gracias por completar la encuesta!',
      step: 'de',
      completed: '¡Encuesta Completada!',
      completedMessage: 'Has completado exitosamente nuestra encuesta AI. Tus respuestas nos ayudarán a crear mejores herramientas.',
      submitButton: 'Enviar Respuestas',
      submitting: 'Enviando...',
      submitted: '¡Enviado correctamente!',
      recaptchaText: 'Completa la verificación reCAPTCHA para enviar tus respuestas',
      privacyPolicy: 'Acepto que mis datos puedan ser utilizados para perfilar productos de Zenit AI Lab',
      goHome: 'Ir al Inicio'
    },
    en: {
      title: 'Zenit AI Survey',
      subtitle: 'Zenit AI Lab is a technology start-up that designs and develops accessible and innovative AI solutions. This survey aims to gather information that will allow us to better understand the automation needs of professionals and companies, enabling us to improve our products. Thank you very much for your time.',
      startButton: 'Start Survey',
      continueButton: 'Continue',
      previous: 'Previous',
      selectOption: 'Select an option:',
      selectMultiple: 'Select all options that apply (you can drag to reorder):',
      selectRating: 'Select your rating:',
      placeholder: 'Write your answer...',
      otherPlaceholder: 'Please specify...',
      thankYou: 'Thank you for completing the survey!',
      step: 'of',
      completed: 'Survey Completed!',
      completedMessage: 'You have successfully completed our AI survey. Your responses will help us create better tools.',
      submitButton: 'Submit Answers',
      submitting: 'Submitting...',
      submitted: 'Successfully submitted!',
      recaptchaText: 'Complete the reCAPTCHA verification to submit your responses',
      privacyPolicy: 'I agree that my data may be used to profile Zenit AI Lab products',
      goHome: 'Go Home'
    }
  };
  
  // Cargar preguntas desde JSON
  const questions = surveyData.questions;

  const startSubtitleTyping = () => {
    // Evitar múltiples ejecuciones y limpiar estado anterior
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = null;
    }
    
    hasStartedTyping.current = true;
    const subtitleText = texts[language].subtitle;
    setIsTypingSubtitle(true);
    setDisplayedSubtitle('');
    setSubtitleCompleted(false);
    setShowContinueButton(false);
    
    let index = 0;
    typingIntervalRef.current = setInterval(() => {
      if (index < subtitleText.length) {
        setDisplayedSubtitle(subtitleText.slice(0, index + 1));
        index++;
      } else {
        clearInterval(typingIntervalRef.current);
        typingIntervalRef.current = null;
        setIsTypingSubtitle(false);
        setTimeout(() => {
          setSubtitleCompleted(true);
          setShowContinueButton(true);
        }, 500);
      }
    }, 15);
  };

  // Iniciar el efecto de escritura del subtítulo cuando se carga el componente
  useEffect(() => {
    if (!gameStarted && !hasStartedTyping.current) {
      const timer = setTimeout(() => {
        startSubtitleTyping();
      }, 500); // Esperar solo 0.5 segundos después de que aparezca el título
      
      return () => {
        clearTimeout(timer);
        if (typingIntervalRef.current) {
          clearInterval(typingIntervalRef.current);
        }
      };
    }
  }, [gameStarted]);

  // Manejar cambio de idioma
  useEffect(() => {
    if (!gameStarted) {
      // Resetear completamente cuando cambia el idioma
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
        typingIntervalRef.current = null;
      }
      
      // Resetear todos los estados
      hasStartedTyping.current = false;
      setDisplayedSubtitle('');
      setSubtitleCompleted(false);
      setIsTypingSubtitle(false);
      setShowContinueButton(false);
      
      // Esperar un poco más para asegurar que el reseteo se complete
      const timer = setTimeout(() => {
        startSubtitleTyping();
      }, 100);
      
      return () => {
        clearTimeout(timer);
        if (typingIntervalRef.current) {
          clearInterval(typingIntervalRef.current);
          typingIntervalRef.current = null;
        }
      };
    }
  }, [language]);

  // Limpiar intervalos al desmontar el componente
  useEffect(() => {
    return () => {
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
      }
    };
  }, []);

  const startTypingEffect = () => {
    const currentQ = questions[currentQuestion];
    setShowTypingIndicator(true);
    
    if (currentQuestion === 0 && showWelcomeMessage) {
      // Para la primera vez, solo mostrar mensaje de bienvenida
      const welcomeMessage = typeof currentQ.aiMessage === 'object' ? currentQ.aiMessage[language] : currentQ.aiMessage;
      
      let messageIndex = 0;
      const messageInterval = setInterval(() => {
        if (messageIndex < welcomeMessage.length) {
          setDisplayedMessage(welcomeMessage.slice(0, messageIndex + 1));
          messageIndex++;
        } else {
          clearInterval(messageInterval);
          setIsTyping(false);
          setShowTypingIndicator(false);
          setWelcomeMessageCompleted(true);
          setTimeout(() => {
            setShowContinueButton(true);
          }, 500);
        }
      }, 15);
    } else {
      // Para todas las preguntas, solo escribir la pregunta
      const fullQuestion = typeof currentQ.question === 'object' ? currentQ.question[language] : currentQ.question;
      let questionIndex = 0;
      const questionInterval = setInterval(() => {
        if (questionIndex < fullQuestion.length) {
          setDisplayedQuestion(fullQuestion.slice(0, questionIndex + 1));
          questionIndex++;
        } else {
          clearInterval(questionInterval);
          setIsTyping(false);
          setShowTypingIndicator(false);
          setTimeout(() => setShowOptions(true), 200);
        }
      }, 10);
    }
  };

  const handleContinueFromWelcome = () => {
    setShowWelcomeMessage(false);
    setShowContinueButton(false);
    setDisplayedMessage('');
    setDisplayedQuestion('');
    setShowOptions(false);
    setIsTyping(true);
    setShowTypingIndicator(true);
    
    // Empezar a escribir la primera pregunta directamente
    const currentQ = questions[currentQuestion];
    const fullQuestion = typeof currentQ.question === 'object' ? currentQ.question[language] : currentQ.question;
    
    let questionIndex = 0;
    const questionInterval = setInterval(() => {
      if (questionIndex < fullQuestion.length) {
        setDisplayedQuestion(fullQuestion.slice(0, questionIndex + 1));
        questionIndex++;
      } else {
        clearInterval(questionInterval);
        setIsTyping(false);
        setShowTypingIndicator(false);
        setTimeout(() => setShowOptions(true), 200);
      }
    }, 10);
  };

  // Iniciar efecto solo cuando comience el juego por primera vez
  useEffect(() => {
    if (gameStarted && currentQuestion === 0 && showWelcomeMessage && !hasInitializedTyping) {
      setHasInitializedTyping(true);
      startTypingEffect();
    }
  }, [gameStarted, hasInitializedTyping]);

  // Manejar cambio de idioma cuando el juego ya está iniciado
  useEffect(() => {
    if (gameStarted && !isTyping) {
      // Si no se está escribiendo, actualizar inmediatamente el contenido
      const currentQ = questions[currentQuestion];
      
      if (currentQuestion === 0) {
        setDisplayedMessage(typeof currentQ.aiMessage === 'object' ? currentQ.aiMessage[language] : currentQ.aiMessage);
      } else {
        setDisplayedMessage('');
      }
      
      setDisplayedQuestion(typeof currentQ.question === 'object' ? currentQ.question[language] : currentQ.question);
    }
  }, [language]);

  const handleAnswer = (answer) => {
    const currentQ = questions[currentQuestion];
    
    // Manejar pregunta de edad especialmente
    if (currentQ.type === 'age-followup') {
      const parentQuestionId = currentQ.parentQuestionId;
      const parentAnswer = answers[parentQuestionId];
      
      // Extraer solo el valor de la profesión (no la estructura completa)
      let professionValue;
      if (typeof parentAnswer === 'string') {
        // Si es un string simple, usarlo directamente
        professionValue = parentAnswer;
      } else if (parentAnswer && typeof parentAnswer === 'object') {
        // Si es un objeto, extraer solo la profesión original
        if (parentAnswer.profession && typeof parentAnswer.profession === 'string') {
          professionValue = parentAnswer.profession;
        } else if (parentAnswer.profession && typeof parentAnswer.profession === 'object') {
          // Si está anidado, extraer el valor más profundo
          professionValue = parentAnswer.profession.profession || parentAnswer.profession;
        } else {
          professionValue = parentAnswer;
        }
      } else {
        professionValue = parentAnswer;
      }
      
      // Combinar profesión y edad
      const combinedAnswer = {
        profession: professionValue,
        age: answer
      };
      
      // Guardar en la pregunta padre (pregunta 2) para el backend
      // Y también guardar en el ID actual para que la validación funcione
      setAnswers({ 
        ...answers, 
        [parentQuestionId]: combinedAnswer,
        [currentQ.id]: answer // Para que funcione la validación del botón continuar
      });
      return;
    }
    
    if (currentQ.type === 'multiple-select') {
      const currentAnswers = answers[currentQ.id] || [];
      let newAnswers;
      
      if (currentAnswers.includes(answer)) {
        // Si ya está seleccionado, lo removemos
        newAnswers = currentAnswers.filter(item => item !== answer);
      } else {
        // Si no está seleccionado, lo agregamos
        newAnswers = [...currentAnswers, answer];
      }
      
      setAnswers({ ...answers, [currentQ.id]: newAnswers });
    } else {
      setAnswers({ ...answers, [currentQ.id]: answer });
    }
  };

  // Función para extraer el email de las respuestas
  const getUserEmail = () => {
    console.log('🔍 Buscando email en answers:', answers);
    
    // Buscar en todas las respuestas una que tenga email
    for (const [questionId, answer] of Object.entries(answers)) {
      console.log(`📝 Checking question ${questionId}:`, answer);
      if (answer && typeof answer === 'object' && answer.email) {
        console.log('✅ Email encontrado:', answer.email);
        return answer.email;
      }
    }
    
    console.log('⚠️ No se encontró email, usando vacío');
    return ''; // Retornar string vacío si no se encuentra email
  };

  const handleReorder = (dragIndex, hoverIndex) => {
    const currentQ = questions[currentQuestion];
    const currentAnswers = answers[currentQ.id] || [];
    const dragItem = currentAnswers[dragIndex];
    
    const newAnswers = [...currentAnswers];
    newAnswers.splice(dragIndex, 1);
    newAnswers.splice(hoverIndex, 0, dragItem);
    
    setAnswers({ ...answers, [currentQ.id]: newAnswers });
  };

  const handleOtherInput = (questionId, value) => {
    setOtherInputs({ ...otherInputs, [questionId]: value });
  };

  const isOtherSelected = (questionId) => {
    const answer = answers[questionId];
    if (Array.isArray(answer)) {
      return answer.some(a => a === 'otro' || a === 'otra' || a === 'otros' || a === 'otras');
    }
    return answer === 'otro' || answer === 'otra' || answer === 'otros' || answer === 'otras';
  };

  const nextQuestion = () => {
    const currentQ = questions[currentQuestion];
    
    // Usar la función canProceed para validar
    if (!canProceed()) {
      return; // No hacer nada si no se puede proceder
    }
    
    // Si estamos en la pregunta 2 (profesión) y la siguiente es la pregunta de edad
    if (currentQ.id === 2) {
      // Encontrar la pregunta de edad
      const ageQuestionIndex = questions.findIndex(q => q.type === 'age-followup' && q.parentQuestionId === 2);
      if (ageQuestionIndex !== -1) {
        // Ir directamente a la pregunta de edad
        setShowOptions(false);
        setIsTyping(true);
        setDisplayedMessage('');
        setDisplayedQuestion('');
        setShowTypingIndicator(true);
        setIsAgeQuestion(true);
        
        setCurrentQuestion(ageQuestionIndex);
        
        // Iniciar typing effect para la pregunta de edad
        setTimeout(() => {
          const ageQ = questions[ageQuestionIndex];
          const fullQuestion = typeof ageQ.question === 'object' ? ageQ.question[language] : ageQ.question;
          let questionIndex = 0;
          const questionInterval = setInterval(() => {
            if (questionIndex < fullQuestion.length) {
              setDisplayedQuestion(fullQuestion.slice(0, questionIndex + 1));
              questionIndex++;
            } else {
              clearInterval(questionInterval);
              setIsTyping(false);
              setShowTypingIndicator(false);
              setTimeout(() => setShowOptions(true), 200);
            }
          }, 15);
        }, 300);
        return;
      }
    }
    
    // Si estamos en una pregunta de edad, ir a la siguiente pregunta real
    if (currentQ.type === 'age-followup') {
      // Encontrar la siguiente pregunta que no sea de edad
      let nextRealQuestionIndex = currentQuestion + 1;
      while (nextRealQuestionIndex < questions.length && questions[nextRealQuestionIndex].type === 'age-followup') {
        nextRealQuestionIndex++;
      }
      
      if (nextRealQuestionIndex < questions.length) {
        setShowOptions(false);
        setIsTyping(true);
        setDisplayedMessage('');
        setDisplayedQuestion('');
        setShowTypingIndicator(true);
        setIsAgeQuestion(false);
        
        setCurrentQuestion(nextRealQuestionIndex);
        
        // Iniciar typing effect para la nueva pregunta
        setTimeout(() => {
          const nextQ = questions[nextRealQuestionIndex];
          const fullQuestion = typeof nextQ.question === 'object' ? nextQ.question[language] : nextQ.question;
          let questionIndex = 0;
          const questionInterval = setInterval(() => {
            if (questionIndex < fullQuestion.length) {
              setDisplayedQuestion(fullQuestion.slice(0, questionIndex + 1));
              questionIndex++;
            } else {
              clearInterval(questionInterval);
              setIsTyping(false);
              setShowTypingIndicator(false);
              setTimeout(() => setShowOptions(true), 200);
            }
          }, 15);
        }, 300);
        return;
      } else {
        // Encuesta completada
        setSurveyCompleted(true);
        return;
      }
    }
    
    if (currentQuestion < questions.length - 1) {
      // Limpiar estado antes de cambiar de pregunta
      setShowOptions(false);
      setIsTyping(true);
      setDisplayedMessage('');
      setDisplayedQuestion('');
      setShowTypingIndicator(true);
      
      // Cambiar a la siguiente pregunta y reiniciar el efecto de escritura
      setCurrentQuestion(prev => {
        const newQuestion = prev + 1;
        
        // Iniciar typing effect para la nueva pregunta después de un pequeño delay
        setTimeout(() => {
          const currentQ = questions[newQuestion];
          
          // Para preguntas 2+, solo escribir la pregunta sin mensaje de bienvenida
          const fullQuestion = typeof currentQ.question === 'object' ? currentQ.question[language] : currentQ.question;
          let questionIndex = 0;
          const questionInterval = setInterval(() => {
            if (questionIndex < fullQuestion.length) {
              setDisplayedQuestion(fullQuestion.slice(0, questionIndex + 1));
              questionIndex++;
            } else {
              clearInterval(questionInterval);
              setIsTyping(false);
              setShowTypingIndicator(false);
              setTimeout(() => setShowOptions(true), 200);
            }
          }, 15);
        }, 300);
        
        return newQuestion;
      });
    } else {
      // Encuesta completada
      setSurveyCompleted(true);
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      const currentQ = questions[currentQuestion];
      
      setShowOptions(false);
      setIsTyping(false);
      setDisplayedMessage('');
      setDisplayedQuestion('');
      setShowTypingIndicator(false);
      
      // Si estamos en una pregunta de edad, volver a la pregunta padre (profesión)
      if (currentQ.type === 'age-followup') {
        const parentQuestionIndex = questions.findIndex(q => q.id === currentQ.parentQuestionId);
        if (parentQuestionIndex !== -1) {
          setCurrentQuestion(parentQuestionIndex);
          setIsAgeQuestion(false);
          
          setTimeout(() => {
            const parentQ = questions[parentQuestionIndex];
            setDisplayedQuestion(typeof parentQ.question === 'object' ? parentQ.question[language] : parentQ.question);
            setShowOptions(true);
          }, 100);
          return;
        }
      }
      
      // Si estamos en la pregunta 3 y la anterior es la pregunta de edad, saltar la edad y ir a la pregunta 2
      if (currentQ.id === 3) {
        const professionQuestionIndex = questions.findIndex(q => q.id === 2);
        if (professionQuestionIndex !== -1) {
          setCurrentQuestion(professionQuestionIndex);
          setIsAgeQuestion(false);
          
          setTimeout(() => {
            const professionQ = questions[professionQuestionIndex];
            setDisplayedQuestion(typeof professionQ.question === 'object' ? professionQ.question[language] : professionQ.question);
            setShowOptions(true);
          }, 100);
          return;
        }
      }

      // Navegación normal hacia atrás
      const newCurrentQuestion = currentQuestion - 1;
      setCurrentQuestion(newCurrentQuestion);
      
      // Mostrar inmediatamente el contenido de la pregunta anterior (SIN efecto de escritura)
      const prevQ = questions[newCurrentQuestion];
      
      setTimeout(() => {
        // Solo mostrar mensaje de bienvenida si vuelves a la pregunta 1
        if (newCurrentQuestion === 0) {
          setDisplayedMessage(typeof prevQ.aiMessage === 'object' ? prevQ.aiMessage[language] : prevQ.aiMessage);
        } else {
          setDisplayedMessage(''); // No mostrar mensaje de bienvenida en otras preguntas
        }
        
        setDisplayedQuestion(typeof prevQ.question === 'object' ? prevQ.question[language] : prevQ.question);
        setShowOptions(true);
      }, 100);
    }
  };




  const renderChatInput = () => {
    const question = questions[currentQuestion];
    
    return (
      <div className="animate-fade-in-up">
        {/* Input box estilo chat - SIN hover/focus en el contenedor */}
        <div className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-lg border border-gray-200">
          <div className="flex items-end space-x-2 sm:space-x-3">
            <div className="flex-1">
              {showOptions ? (
                <>
                  {question.type === 'multiple-choice' ? (
                    <div className="space-y-2 sm:space-y-3">
                      <p className="text-gray-500 text-sm sm:text-base mb-2 sm:mb-3 font-medium">{texts[language].selectOption}</p>
                      <div className="space-y-1.5 sm:space-y-2 max-h-32 sm:max-h-36 lg:max-h-40 overflow-y-auto pr-1 sm:pr-2 custom-scrollbar">
                        {getOptionsForQuestion(question).map((option, index) => (
                          <button
                            key={option.value}
                            className={`
                              w-full p-3 sm:p-4 text-left rounded-lg transition-all duration-300 text-sm sm:text-base transform hover:scale-[1.02] animate-fade-in-scale
                              ${answers[question.id] === option.value 
                                ? 'bg-blue-600 text-white shadow-lg' 
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }
                            `}
                            onClick={() => handleAnswer(option.value)}
                            style={{ animationDelay: `${index * 100}ms` }}
                          >
                            {typeof option.label === 'object' ? option.label[language] : option.label}
                          </button>
                        ))}
                      </div>
                      



                      
                      {/* Campo de texto para "Otro" - multiple-choice */}
                      {isOtherSelected(question.id) && (
                        <div className="mt-3 sm:mt-4 animate-fade-in-scale">
                          <input
                            type="text"
                            placeholder={texts[language].otherPlaceholder}
                            value={otherInputs[question.id] || ''}
                            onChange={(e) => handleOtherInput(question.id, e.target.value)}
                            className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                          />
                        </div>
                      )}
                    </div>
                  ) : question.type === 'multiple-select' ? (
                    <div className="space-y-2 sm:space-y-3">
                      <p className="text-gray-500 text-sm sm:text-base mb-2 sm:mb-3 font-medium">{texts[language].selectMultiple}</p>
                      



                      {/* Opciones seleccionadas (ordenables) */}
                      {answers[question.id] && answers[question.id].length > 0 && (
                        <div className="mb-3 sm:mb-4">
                          <p className="text-xs text-gray-400 mb-2">
                            {language === 'es' ? 'Seleccionadas (arrastra para reordenar por prioridad):' : 'Selected (drag to reorder by priority):'}
                          </p>
                          <div className="space-y-1.5 sm:space-y-2 max-h-32 lg:max-h-40 overflow-y-auto pr-1 sm:pr-2 custom-scrollbar">
                            {answers[question.id].map((selectedValue, index) => {
                              const option = question.options.find(opt => opt.value === selectedValue);
                              return (
                                <div
                                  key={selectedValue}
                                  className="flex items-center justify-between p-2.5 sm:p-3 bg-blue-600 text-white rounded-lg shadow-lg cursor-move"
                                  draggable
                                  onDragStart={(e) => e.dataTransfer.setData('text/plain', index)}
                                  onDragOver={(e) => e.preventDefault()}
                                  onDrop={(e) => {
                                    e.preventDefault();
                                    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
                                    handleReorder(dragIndex, index);
                                  }}
                                >
                                  <span className="flex items-center">
                                    <span className="font-bold mr-2 text-blue-200 text-base sm:text-lg">#{index + 1}</span>
                                    <span className="text-sm sm:text-base">{typeof option?.label === 'object' ? option.label[language] : option?.label}</span>
                                  </span>
                                  <button
                                    onClick={() => handleAnswer(selectedValue)}
                                    className="ml-2 text-blue-200 hover:text-white transition-colors text-lg"
                                  >
                                    ✕
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Todas las opciones disponibles */}
                      <div className="space-y-1.5 sm:space-y-2 max-h-32 sm:max-h-36 lg:max-h-40 overflow-y-auto pr-1 sm:pr-2 custom-scrollbar">
                        {getOptionsForQuestion(question).map((option, index) => {
                          const isSelected = answers[question.id]?.includes(option.value);
                          return (
                            <button
                              key={option.value}
                              className={`
                                w-full p-2.5 sm:p-3 text-left rounded-lg transition-all duration-300 text-sm sm:text-base transform hover:scale-[1.02] animate-fade-in-scale
                                ${isSelected
                                  ? 'bg-green-500 text-white shadow-lg' 
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }
                              `}
                              onClick={() => handleAnswer(option.value)}
                              style={{ animationDelay: `${index * 100}ms` }}
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-sm sm:text-base">
                                  {typeof option.label === 'object' ? option.label[language] : option.label}
                                </span>
                                {isSelected && <span className="text-green-200 text-lg sm:text-xl">✓</span>}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      
                      {/* Campo de texto para "Otro" - multiple-select */}
                      {isOtherSelected(question.id) && (
                        <div className="mt-3 sm:mt-4 animate-fade-in-scale">
                          <input
                            type="text"
                            placeholder={texts[language].otherPlaceholder}
                            value={otherInputs[question.id] || ''}
                            onChange={(e) => handleOtherInput(question.id, e.target.value)}
                            className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                          />
                        </div>
                      )}
                    </div>
                  ) : question.type === 'conditional-email' ? (
                    <div className="space-y-2 sm:space-y-3">
                      <p className="text-gray-500 text-sm sm:text-base mb-2 sm:mb-3 font-medium">{texts[language].selectOption}</p>
                      <div className="space-y-1.5 sm:space-y-2">
                        {getOptionsForQuestion(question).map((option, index) => (
                          <button
                            key={option.value}
                            className={`
                              w-full p-3 sm:p-4 text-left rounded-lg transition-all duration-300 text-sm sm:text-base transform hover:scale-[1.02] animate-fade-in-scale
                              ${answers[question.id]?.choice === option.value 
                                ? 'bg-blue-600 text-white shadow-lg' 
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }
                            `}
                            onClick={() => handleAnswer({choice: option.value, email: answers[question.id]?.email || ''})}
                            style={{ animationDelay: `${index * 100}ms` }}
                          >
                            {typeof option.label === 'object' ? option.label[language] : option.label}
                          </button>
                        ))}
                      </div>
                      
                      {/* Campo de email condicional */}
                      {answers[question.id]?.choice === 'si' && (
                        <div className="mt-3 sm:mt-4 space-y-2 animate-fade-in-scale">
                          <p className="text-gray-600 text-xs sm:text-sm font-medium">
                            {question.emailField?.label ? (typeof question.emailField.label === 'object' ? question.emailField.label[language] : question.emailField.label) : 'Email:'}
                            <span className="text-red-500 ml-1">*</span>
                          </p>
                          <div className="relative">
                            <input
                              type="email"
                              className={`survey-textarea w-full p-2.5 sm:p-3 border rounded-lg focus:outline-none focus:ring-2 text-xs sm:text-sm transition-colors ${
                                answers[question.id]?.email && !isValidEmail(answers[question.id].email)
                                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50 text-red-900'
                                  : answers[question.id]?.email && isValidEmail(answers[question.id].email)
                                  ? 'border-green-300 focus:ring-green-500 focus:border-green-500 bg-green-50 text-green-900'
                                  : 'border-gray-300 focus:ring-blue-500 focus:border-transparent text-gray-900'
                              }`}
                              placeholder={question.emailField?.placeholder ? (typeof question.emailField.placeholder === 'object' ? question.emailField.placeholder[language] : question.emailField.placeholder) : 'email@ejemplo.com'}
                              value={answers[question.id]?.email || ''}
                              onChange={(e) => handleAnswer({choice: answers[question.id].choice, email: e.target.value})}
                            />
                            {/* Indicador visual del estado del email */}
                            {answers[question.id]?.email && (
                              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                {isValidEmail(answers[question.id].email) ? (
                                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : (
                                  <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                )}
                              </div>
                            )}
                          </div>
                          {/* Mensaje de ayuda */}
                          {answers[question.id]?.email && !isValidEmail(answers[question.id].email) && (
                            <p className="text-red-500 text-xs animate-fade-in">
                              {language === 'es' ? 'Por favor, ingresa un email válido (ejemplo: usuario@dominio.com)' : 'Please enter a valid email (example: user@domain.com)'}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ) : question.type === 'single-select' ? (
                    <div className="space-y-2 sm:space-y-3">
                      <p className="text-gray-500 text-sm sm:text-base mb-2 sm:mb-3 font-medium">{texts[language].selectOption}</p>
                      <div className="space-y-1.5 sm:space-y-2 max-h-32 sm:max-h-36 lg:max-h-40 overflow-y-auto pr-1 sm:pr-2 custom-scrollbar">
                        {getOptionsForQuestion(question).map((option, index) => (
                          <button
                            key={option.value}
                            className={`
                              w-full p-3 sm:p-4 text-left rounded-lg transition-all duration-300 text-sm sm:text-base transform hover:scale-[1.02] animate-fade-in-scale
                              ${answers[question.id] === option.value 
                                ? 'bg-blue-600 text-white shadow-lg border-2 border-blue-700' 
                                : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                              }
                            `}
                            onClick={() => handleAnswer(option.value)}
                            style={{ animationDelay: `${index * 100}ms` }}
                          >
                            {typeof option.label === 'object' ? option.label[language] : option.label}
                          </button>
                        ))}
                      </div>
                      
                      {/* Campo de texto para "Otro" - single-select */}
                      {isOtherSelected(question.id) && (
                        <div className="mt-3 animate-fade-in">
                          <textarea
                            className="survey-textarea w-full p-2.5 sm:p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-sm"
                            rows="2"
                            placeholder={language === 'es' ? 'Especifica...' : 'Specify...'}
                            value={otherInputs[question.id] || ''}
                            onChange={(e) => handleOtherInput(question.id, e.target.value)}
                          />
                        </div>
                      )}
                    </div>
                  ) : question.type === 'age-followup' ? (
                    <div className="space-y-2 sm:space-y-3">
                      <p className="text-gray-500 text-sm sm:text-base mb-2 sm:mb-3 font-medium">{texts[language].selectOption}</p>
                      <div className="space-y-1.5 sm:space-y-2 max-h-32 sm:max-h-36 lg:max-h-40 overflow-y-auto pr-1 sm:pr-2 custom-scrollbar">
                        {getOptionsForQuestion(question).map((option, index) => (
                          <button
                            key={option.value}
                            className={`
                              w-full p-3 sm:p-4 text-left rounded-lg transition-all duration-300 text-sm sm:text-base transform hover:scale-[1.02] animate-fade-in-scale
                              ${answers[question.id] === option.value 
                                ? 'bg-blue-600 text-white shadow-lg' 
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }
                            `}
                            onClick={() => handleAnswer(option.value)}
                            style={{ animationDelay: `${index * 100}ms` }}
                          >
                            {typeof option.label === 'object' ? option.label[language] : option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : question.type === 'rating' ? (
                    <div className="space-y-2 sm:space-y-3">
                      <p className="text-gray-500 text-xs sm:text-sm">{texts[language].selectRating}</p>
                      <div className="flex space-x-1.5 sm:space-x-2 justify-center">
                        {[...Array(question.scale)].map((_, index) => (
                          <button
                            key={index + 1}
                            className={`
                              w-8 h-8 sm:w-10 sm:h-10 rounded-full transition-all duration-300 font-medium transform hover:scale-110 animate-fade-in-scale text-sm sm:text-base
                              ${answers[question.id] >= index + 1
                                ? 'bg-blue-600 text-white shadow-lg' 
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }
                            `}
                            onClick={() => handleAnswer(index + 1)}
                            style={{ animationDelay: `${index * 100}ms` }}
                          >
                            {index + 1}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 sm:space-y-3">
                      <textarea
                        className="survey-textarea w-full p-2.5 sm:p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-sm"
                        rows="3"
                        placeholder={typeof question.placeholder === 'object' ? question.placeholder[language] : texts[language].placeholder}
                        value={answers[question.id] || ''}
                        onChange={(e) => handleAnswer(e.target.value)}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center py-6">
                  <div className="text-gray-500 text-sm">
                    {language === 'es' ? 'Las opciones aparecerán aquí...' : 'Options will appear here...'}
                  </div>
                </div>
              )}
            </div>
            
            {/* Botón de enviar */}
            <div className="flex-shrink-0">
              <button
                onClick={nextQuestion}
                disabled={!canProceed()}
                className={`
                  p-2.5 sm:p-3 rounded-full transition-all duration-200 transform hover:scale-110 
                  ${canProceed()
                    ? 'bg-blue-600 text-white shadow-lg hover:bg-blue-700' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }
                `}
              >
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const startGame = () => {
    setGameStarting(true);
    
    // Efecto de transición épico
    setTimeout(() => {
      setGameStarted(true);
      setCurrentQuestion(0);
      setAnswers({});
      setShowOptions(false);
      setIsTyping(true);
      setDisplayedMessage('');
      setDisplayedQuestion('');
      setShowTypingIndicator(false);
      setGameStarting(false);
      // Resetear estados del mensaje de bienvenida
      setShowWelcomeMessage(true);
      setWelcomeMessageCompleted(false);
      setShowContinueButton(false);
      setHasInitializedTyping(false);
    }, 2000); // 2 segundos de transición épica
  };

  // Calcular progreso excluyendo preguntas de edad
  const totalRealQuestions = questions.filter(q => q.type !== 'age-followup').length;
  const currentRealQuestionIndex = questions.slice(0, currentQuestion + 1).filter(q => q.type !== 'age-followup').length;
  const progress = (currentRealQuestionIndex / totalRealQuestions) * 100;

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 survey-container">
      
      {/* Botón de cambio de idioma - Posición fija (solo visible durante el survey) */}
      {gameStarted && (
        <div className="fixed top-4 right-4 z-50">
          <button
            onClick={() => setLanguage(language === 'es' ? 'en' : 'es')}
            className="px-3 py-2 bg-white/90 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 text-sm font-medium text-gray-700 border border-gray-200/50 backdrop-blur-sm"
          >
            {language === 'es' ? '🇪🇸 ES' : '🇺🇸 EN'}
          </button>
        </div>
      )}

      {gameStarting ? (
        /* Transición épica de inicio */
        <div className="relative w-full h-full flex items-start justify-center overflow-hidden bg-black pt-40">
          {/* Efecto de energía expansiva */}
          <div className="absolute inset-0">
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <div className="w-4 h-4 bg-cyan-400 rounded-full animate-ping" style={{ animationDuration: '0.5s' }}></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-blue-500 rounded-full animate-ping opacity-75" style={{ animationDuration: '0.8s', animationDelay: '0.2s' }}></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-purple-500 rounded-full animate-ping opacity-50" style={{ animationDuration: '1.2s', animationDelay: '0.4s' }}></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-cyan-400 rounded-full animate-ping opacity-25" style={{ animationDuration: '1.6s', animationDelay: '0.6s' }}></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-blue-500 rounded-full animate-ping opacity-10" style={{ animationDuration: '2s', animationDelay: '0.8s' }}></div>
            </div>
          </div>

          {/* Texto de carga */}
          <div className="relative z-10 text-center">
            <h2 className="text-4xl md:text-6xl font-black bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent mb-8 animate-pulse">
              INICIANDO...
            </h2>
            
            {/* Barra de progreso futurista */}
            <div className="w-80 h-2 bg-gray-800 rounded-full overflow-hidden shadow-inner">
              <div className="h-full bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 rounded-full animate-pulse" style={{ 
                animation: 'loadingBar 2s ease-out forwards'
              }}></div>
            </div>
            
            {/* Texto de loading */}
            <p className="text-cyan-300 text-lg mt-4 animate-pulse">
              Preparing AI Experience...
            </p>
          </div>

          {/* Líneas de energía */}
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="absolute bg-gradient-to-r from-transparent via-cyan-400 to-transparent h-0.5 w-full animate-pulse opacity-30"
                style={{
                  top: `${10 + i * 10}%`,
                  animationDelay: `${i * 0.2}s`,
                  animationDuration: '1.5s'
                }}
              />
            ))}
          </div>
        </div>
      ) : !gameStarted ? (
        /* Pantalla de inicio épica estilo videojuego */
        <div className="relative w-full h-full flex items-start justify-center overflow-hidden pt-8">
          {/* Fondo animado con partículas */}
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-blue-900 to-black">
            {/* Partículas flotantes */}
            <div className="absolute inset-0">
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-1 h-1 bg-white rounded-full opacity-30 animate-pulse"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 3}s`,
                    animationDuration: `${2 + Math.random() * 3}s`
                  }}
                />
              ))}
            </div>
            
            {/* Ondas de energía */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full border-2 border-cyan-400 animate-ping" style={{ animationDuration: '3s' }}></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 rounded-full border border-blue-400 animate-ping" style={{ animationDuration: '4s', animationDelay: '1s' }}></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full border border-purple-400 animate-ping" style={{ animationDuration: '5s', animationDelay: '2s' }}></div>
            </div>
          </div>

          {/* Contenido principal */}
          <div className="relative z-10 text-center max-w-4xl mx-auto px-8 space-y-6 mt-8">
            {/* Logo/Título con efecto holográfico */}
            <div className="relative">
              <h1 className="relative text-6xl md:text-7xl font-black text-white tracking-wider uppercase animate-pulse">
                {texts[language].title}
              </h1>
              
              {/* Líneas de energía alrededor del título */}
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-full h-2 bg-gradient-to-r from-transparent via-cyan-400 to-transparent animate-pulse"></div>
              <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-full h-2 bg-gradient-to-r from-transparent via-purple-400 to-transparent animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>
            
            {/* Selector de idioma elegante */}
            <div className="flex justify-center items-center space-x-4 my-8">
              <span className="text-gray-400 text-sm font-medium uppercase tracking-wider">Language:</span>
              <div className="flex bg-gray-800/50 rounded-full p-1 backdrop-blur-sm border border-gray-600/50">
                <button
                  onClick={() => setLanguage('es')}
                  className={`px-4 py-2 rounded-full font-medium text-sm transition-all duration-300 flex items-center space-x-2 ${
                    language === 'es' 
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg transform scale-105' 
                      : 'text-gray-300 hover:text-white hover:bg-gray-700/50'
                  }`}
                >
                  <span className="text-lg">🇪🇸</span>
                  <span>Español</span>
                </button>
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-4 py-2 rounded-full font-medium text-sm transition-all duration-300 flex items-center space-x-2 ${
                    language === 'en' 
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg transform scale-105' 
                      : 'text-gray-300 hover:text-white hover:bg-gray-700/50'
                  }`}
                >
                  <span className="text-lg">🇺🇸</span>
                  <span>English</span>
                </button>
              </div>
            </div>
            
            {/* Subtítulo con efecto de typing */}
            <div className="relative overflow-hidden">
              <p className="text-xl md:text-2xl text-gray-300 leading-relaxed font-light tracking-wide min-h-[80px]">
                {displayedSubtitle}
                {isTypingSubtitle && (
                  <span className="inline-block w-0.5 h-6 bg-cyan-400 ml-1 animate-pulse"></span>
                )}
              </p>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 -skew-x-12 translate-x-full animate-shine"></div>
            </div>
            
            {/* Botón épico de inicio - Solo visible cuando el subtítulo esté completo */}
            {subtitleCompleted && (
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 via-blue-600 to-purple-600 rounded-2xl blur-lg opacity-75 group-hover:opacity-100 transition-opacity duration-500 animate-pulse"></div>
                <button
                  onClick={startGame}
                  className="relative w-64 bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-700 text-white px-10 py-4 rounded-2xl font-bold text-xl uppercase tracking-widest shadow-2xl transform hover:scale-105 transition-all duration-500 border-2 border-cyan-400 hover:border-white group animate-fadeInUp"
                  style={{ animation: 'fadeInUp 0.8s ease-out forwards' }}
                >
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 rounded-2xl transition-opacity duration-500"></div>
                  <span className="relative z-10 flex items-center justify-center space-x-3">
                    <span>{texts[language].startButton}</span>
                    <svg className="w-6 h-6 transform group-hover:translate-x-2 transition-transform duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </span>
                  
                  {/* Efecto de energía en el botón */}
                  <div className="absolute top-0 left-0 w-full h-full overflow-hidden rounded-2xl">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white to-transparent opacity-50 animate-shimmer"></div>
                    <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white to-transparent opacity-50 animate-shimmer" style={{ animationDelay: '1s' }}></div>
                  </div>
                </button>
              </div>
            )}
          </div>

          {/* Efectos de esquinas */}
          <div className="absolute top-4 left-4 w-16 h-16 border-l-4 border-t-4 border-cyan-400 opacity-50"></div>
          <div className="absolute top-4 right-4 w-16 h-16 border-r-4 border-t-4 border-purple-400 opacity-50"></div>
          <div className="absolute bottom-4 left-4 w-16 h-16 border-l-4 border-b-4 border-blue-400 opacity-50"></div>
          <div className="absolute bottom-4 right-4 w-16 h-16 border-r-4 border-b-4 border-cyan-400 opacity-50"></div>
        </div>
      ) : surveyCompleted ? (
        /* Pantalla de finalización épica */
        <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
          {/* Fondo animado de celebración */}
          <div className="absolute inset-0 bg-gradient-to-br from-green-900 via-blue-900 to-purple-900">
            {/* Partículas de celebración */}
            <div className="absolute inset-0">
              {[...Array(30)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-2 h-2 bg-yellow-400 rounded-full opacity-60 animate-bounce"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 2}s`,
                    animationDuration: `${1 + Math.random() * 2}s`
                  }}
                />
              ))}
            </div>
            
            {/* Ondas de celebración */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full border-2 border-green-400 animate-ping" style={{ animationDuration: '2s' }}></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 rounded-full border border-yellow-400 animate-ping" style={{ animationDuration: '3s', animationDelay: '0.5s' }}></div>
            </div>
          </div>

          {/* Contenido principal */}
          <div className="relative z-10 text-center max-w-4xl mx-auto px-8 space-y-6">
            {/* Ícono de éxito */}
            <div className="relative">
              <div className="w-24 h-24 mx-auto bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center shadow-2xl animate-pulse">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="absolute inset-0 w-24 h-24 mx-auto bg-gradient-to-r from-green-400 to-blue-500 rounded-full opacity-50 animate-ping"></div>
            </div>
            
            {/* Título de completado */}
            <div className="relative">
              <h1 className="text-4xl md:text-5xl font-black bg-gradient-to-r from-green-400 via-blue-500 to-purple-600 bg-clip-text text-transparent tracking-wider uppercase animate-pulse">
                {texts[language].completed}
              </h1>
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-full h-1 bg-gradient-to-r from-transparent via-green-400 to-transparent animate-pulse"></div>
            </div>
            
            {/* Mensaje de agradecimiento */}
            <div className="relative overflow-hidden">
              <p className="text-lg md:text-xl text-gray-300 leading-relaxed font-light tracking-wide">
                {texts[language].completedMessage}
              </p>
            </div>

            {/* Contenedor para reCAPTCHA y botón */}
            <SurveySubmitter
              answers={answers}
              otherInputs={otherInputs}
              isOtherSelected={isOtherSelected}
              privacyAccepted={privacyAccepted}
              setPrivacyAccepted={setPrivacyAccepted}
              email={getUserEmail()}
              onSubmitStart={() => setIsSubmitting(true)}
              onSubmitSuccess={() => {
                setShowSuccessNotification(true);
                setTimeout(() => {
                  setShowSuccessNotification(false);
                }, 4000);
              }}
              onSubmitError={(error) => {
                console.error('Error en envío:', error);
              }}
              texts={texts}
              language={language}
            />

            {/* Estadísticas de respuestas */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-8">
              <div className="bg-gray-800 bg-opacity-30 rounded-lg p-4 backdrop-blur-sm border border-gray-600">
                <div className="text-2xl font-bold text-green-400">{Object.keys(answers).length}</div>
                <div className="text-gray-300 text-sm">
                  {language === 'es' ? 'Preguntas respondidas' : 'Questions answered'}
                </div>
              </div>
              <div className="bg-gray-800 bg-opacity-30 rounded-lg p-4 backdrop-blur-sm border border-gray-600">
                <div className="text-2xl font-bold text-blue-400">{questions.length}</div>
                <div className="text-gray-300 text-sm">
                  {language === 'es' ? 'Total de preguntas' : 'Total questions'}
                </div>
              </div>
              <div className="bg-gray-800 bg-opacity-30 rounded-lg p-4 backdrop-blur-sm border border-gray-600 col-span-2 md:col-span-1">
                <div className="text-2xl font-bold text-purple-400">100%</div>
                <div className="text-gray-300 text-sm">
                  {language === 'es' ? 'Completado' : 'Completed'}
                </div>
              </div>
            </div>

            {/* Botón para ir al home */}
            <div className="mt-8">
              <button
                onClick={() => window.location.href = '/'}
                className="bg-gradient-to-r from-green-500 to-blue-600 text-white font-semibold px-8 py-4 rounded-xl hover:from-green-600 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 shadow-2xl border-2 border-green-400"
              >
                {language === 'es' ? '🏠 Ir al inicio' : '🏠 Go to home'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Pantalla del juego */
        <div className="w-full max-w-4xl bg-gray-200 rounded-3xl shadow-2xl overflow-hidden neumorphism-container flex flex-col h-[90vh] sm:h-[90vh] mx-2 sm:mx-4 lg:mx-auto">
          
          {/* Header con progreso - Solo mostrar si no estamos en el mensaje de bienvenida */}
          {!showWelcomeMessage && (
            <div className="p-3 sm:p-4 lg:p-6 bg-gray-200 border-b border-gray-300 flex-shrink-0">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <div className="text-gray-700 font-medium text-sm sm:text-base">
                  {texts[language].title}
                </div>
                <div className="text-gray-500 text-xs sm:text-sm">
                  {currentRealQuestionIndex} {texts[language].step} {totalRealQuestions}
                </div>
              </div>
              <div className="w-full bg-gray-300 rounded-full h-2 sm:h-3 overflow-hidden shadow-inner">
                <div 
                  className="h-full rounded-full transition-all duration-500 shadow-lg"
                  style={{ 
                    width: `${progress}%`, 
                    minWidth: progress > 0 ? '8px' : '0px',
                    backgroundColor: '#3b82f6',
                    background: 'linear-gradient(to right, #3b82f6, #1d4ed8)',
                    boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                  }}
                />
              </div>
            </div>
          )}

          {/* Chat Messages Container - Solo pregunta actual */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4">
            {/* AI Message */}
            {displayedMessage && (
              <div className="flex items-start space-x-2 sm:space-x-3 animate-slide-in-right">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-200 rounded-full flex items-center justify-center shadow-lg neumorphism-avatar flex-shrink-0">
                  <img 
                    src="/Logo.webp" 
                    alt="AI" 
                    className="w-5 h-5 sm:w-6 sm:h-6 rounded-full"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="bg-white rounded-2xl rounded-tl-sm p-3 sm:p-4 shadow-lg border border-gray-200 max-w-full sm:max-w-xl">
                    <p className="text-gray-800 text-base sm:text-lg leading-relaxed font-medium">
                      {displayedMessage}
                      {isTyping && currentQuestion === 0 && <span className="typing-cursor">|</span>}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* AI Question */}
            {displayedQuestion && (
              <div className="flex items-start space-x-2 sm:space-x-3 animate-slide-in-right" style={{animationDelay: '0.3s'}}>
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-200 rounded-full flex items-center justify-center shadow-lg neumorphism-avatar flex-shrink-0">
                  <img 
                    src="/Logo.webp" 
                    alt="AI" 
                    className="w-5 h-5 sm:w-6 sm:h-6 rounded-full"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="bg-white rounded-2xl rounded-tl-sm p-3 sm:p-4 shadow-lg border border-gray-200 max-w-full sm:max-w-xl">
                    <p className="text-gray-800 text-base sm:text-lg leading-relaxed font-medium">
                      {displayedQuestion}
                      {isTyping && <span className="typing-cursor">|</span>}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Indicador de "Zenit está escribiendo..." */}
            {showTypingIndicator && (
              <div className="flex items-start space-x-2 sm:space-x-3 animate-slide-in-right mt-2">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-200 rounded-full flex items-center justify-center shadow-lg neumorphism-avatar flex-shrink-0">
                  <img 
                    src="/Logo.webp" 
                    alt="AI" 
                    className="w-5 h-5 sm:w-6 sm:h-6 rounded-full"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 pt-2 sm:pt-3">
                    <span className="text-gray-500 text-xs sm:text-sm font-medium">
                      {language === 'es' ? 'Zenit está escribiendo' : 'Zenit is typing'}
                    </span>
                    <div className="flex space-x-1">
                      <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input Container - Fixed at bottom */}
          <div className="p-3 sm:p-6 bg-gray-200 border-t border-gray-300 flex-shrink-0">
            {showContinueButton ? (
              /* Botón continuar para después del mensaje de bienvenida */
              <div className="flex justify-center">
                <button
                  onClick={handleContinueFromWelcome}
                  className="px-6 sm:px-8 py-2.5 sm:py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-all duration-200 shadow-lg transform hover:scale-105 text-sm sm:text-base"
                >
                  {texts[language].continueButton}
                </button>
              </div>
            ) : (
              <>
                {renderChatInput()}
                
                {/* Navigation buttons */}
                <div className="flex justify-between mt-3 sm:mt-4">
                  <button
                    onClick={prevQuestion}
                    disabled={currentQuestion === 0}
                    className="flex items-center space-x-1.5 sm:space-x-2 px-3 sm:px-4 py-2 rounded-xl transition-all duration-200 font-medium bg-gray-300 text-gray-600 hover:bg-gray-400 hover:text-gray-700 neumorphism-button text-sm sm:text-base"
                    style={{ opacity: currentQuestion === 0 ? 0.5 : 1 }}
                  >
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    <span>{texts[language].previous}</span>
                  </button>
                </div>
              </>
            )}
          </div>
          
        </div>
      )}
      
      {/* Notificación de éxito personalizada */}
      {showSuccessNotification && (
        <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
          <div className="bg-black bg-opacity-50 absolute inset-0"></div>
          <div className="relative z-10 transform animate-bounce">
            <div className="bg-gradient-to-r from-green-500 via-blue-600 to-purple-700 rounded-2xl p-8 shadow-2xl border-2 border-green-400 max-w-md mx-auto">
              <div className="text-center space-y-4">
                {/* Ícono de éxito con animación */}
                <div className="relative">
                  <div className="w-16 h-16 mx-auto bg-white rounded-full flex items-center justify-center shadow-lg animate-pulse">
                    <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="absolute inset-0 w-16 h-16 mx-auto bg-white rounded-full opacity-30 animate-ping"></div>
                </div>
                
                {/* Mensaje de éxito */}
                <div className="space-y-2">
                  <h3 className="text-2xl font-bold text-white">
                    {texts[language].submitted}
                  </h3>
                  <p className="text-green-100 text-sm">
                    {language === 'es' 
                      ? 'Tus respuestas han sido enviadas correctamente'
                      : 'Your responses have been submitted successfully'
                    }
                  </p>
                </div>
                
                {/* Efectos visuales */}
                <div className="absolute top-0 left-0 w-full h-full overflow-hidden rounded-2xl pointer-events-none">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white to-transparent opacity-50 animate-shimmer"></div>
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white to-transparent opacity-50 animate-shimmer" style={{ animationDelay: '1s' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Componente principal con Provider de reCAPTCHA
const SurveyWithRecaptcha = () => {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
      <Survey />
    </GoogleReCaptchaProvider>
  );
};

export default SurveyWithRecaptcha;
